import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DoorOpen, Plus, Loader2, Users, CheckCircle2, ClipboardList, MessageSquareText, ShieldAlert } from 'lucide-react';
import { KpiCard } from '@/components/shared/KpiCard';
import { LocalizedDateInput } from '@/components/shared/LocalizedDateInput';
import { api } from '@/lib/api';
import { DonutCard, ProgressCard, SEM } from '@/components/client/SuiteAnalytics';
import { txError } from '@/lib/txErrors';
import { toast } from 'sonner';
import { TalentExitCaseDetail } from '@/components/client/TalentExitCaseDetail';

export const EXIT_TYPE_CODES = ['voluntary_resignation', 'involuntary_termination', 'retirement', 'end_of_contract', 'mutual_agreement', 'other'];
export const REASON_CODES = ['compensation', 'career_growth', 'work_environment', 'leadership', 'personal', 'relocation', 'performance', 'restructuring', 'retirement', 'other'];

const IV_BADGE = {
  not_started: 'bg-muted text-muted-foreground',
  pending: 'bg-info/10 text-info border-info/20',
  completed: 'bg-success/10 text-success border-success/20',
  recorded: 'bg-success/10 text-success border-success/20',
  waived: 'bg-warning/10 text-warning border-warning/20',
};

function CreateCaseDialog({ open, onOpenChange, employees, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ user_id: '', exit_type: '', reason_category: '', last_working_day: '', notice_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) setForm({ user_id: '', exit_type: '', reason_category: '', last_working_day: '', notice_date: '', notes: '' }); }, [open]);

  const submit = async () => {
    if (!form.user_id || !form.exit_type || !form.reason_category) {
      toast.error(t('talentexit.create.missingFields', { defaultValue: 'Employee, exit type and reason are required.' }));
      return;
    }
    setSaving(true);
    try {
      await api.post('/client/talentexit/cases', {
        ...form,
        last_working_day: form.last_working_day || null,
        notice_date: form.notice_date || null,
        notes: form.notes || null,
      });
      toast.success(t('talentexit.create.created', { defaultValue: 'Exit case created.' }));
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(txError(t, e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="tx-create-dialog">
        <DialogHeader>
          <DialogTitle>{t('talentexit.create.title', { defaultValue: 'New exit case' })}</DialogTitle>
          <DialogDescription>{t('talentexit.create.desc', { defaultValue: 'Open a structured exit process for a departing employee.' })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('talentexit.fields.employee', { defaultValue: 'Employee' })}</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm((f) => ({ ...f, user_id: v }))}>
              <SelectTrigger data-testid="tx-create-employee"><SelectValue placeholder={t('common.select', { defaultValue: 'Select' })} /></SelectTrigger>
              <SelectContent>
                {employees.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} — {u.position || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('talentexit.fields.exitType', { defaultValue: 'Exit type' })}</Label>
              <Select value={form.exit_type} onValueChange={(v) => setForm((f) => ({ ...f, exit_type: v }))}>
                <SelectTrigger data-testid="tx-create-type"><SelectValue placeholder={t('common.select', { defaultValue: 'Select' })} /></SelectTrigger>
                <SelectContent>
                  {EXIT_TYPE_CODES.map((c) => <SelectItem key={c} value={c}>{t(`talentexit.exitTypes.${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('talentexit.fields.reason', { defaultValue: 'Reason category' })}</Label>
              <Select value={form.reason_category} onValueChange={(v) => setForm((f) => ({ ...f, reason_category: v }))}>
                <SelectTrigger data-testid="tx-create-reason"><SelectValue placeholder={t('common.select', { defaultValue: 'Select' })} /></SelectTrigger>
                <SelectContent>
                  {REASON_CODES.map((c) => <SelectItem key={c} value={c}>{t(`talentexit.reasons.${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('talentexit.fields.lastDay', { defaultValue: 'Last working day' })}</Label>
              <LocalizedDateInput value={form.last_working_day} onChange={(v) => setForm((f) => ({ ...f, last_working_day: v }))} data-testid="tx-create-lastday" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('talentexit.fields.noticeDate', { defaultValue: 'Notice date' })}</Label>
              <LocalizedDateInput value={form.notice_date} onChange={(v) => setForm((f) => ({ ...f, notice_date: v }))} data-testid="tx-create-notice" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('talentexit.fields.notes', { defaultValue: 'Notes' })}</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="tx-create-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit} disabled={saving} data-testid="tx-create-submit">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {t('talentexit.create.submit', { defaultValue: 'Create case' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PatternsPanel({ patterns }) {
  const { t } = useTranslation();
  if (!patterns) return null;
  const Section = ({ title, data, labelFn }) => {
    const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...entries.map(([, v]) => v));
    return (
      <Card className="p-5 shadow-card">
        <h4 className="text-sm font-semibold mb-3">{title}</h4>
        {entries.length === 0 && <p className="text-sm text-muted-foreground">{t('talentexit.patterns.empty', { defaultValue: 'No data yet.' })}</p>}
        <div className="space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="text-sm w-52 truncate" title={labelFn ? labelFn(k) : k}>{labelFn ? labelFn(k) : k}</span>
              <div className="flex-1 h-2.5 rounded bg-muted overflow-hidden">
                <div className="h-full bg-primary/70 rounded" style={{ width: `${(v / max) * 100}%` }} />
              </div>
              <span className="text-sm font-medium w-8 text-right">{v}</span>
            </div>
          ))}
        </div>
      </Card>
    );
  };
  return (
    <div className="space-y-4" data-testid="tx-patterns">
      {/* S3 — Exit Analytics: administrative counts vs interview-derived patterns */}
      <div className="p-3 rounded-md border border-border bg-muted/30 text-sm space-y-1">
        <p className="font-semibold">{t('talentexit.analytics.title', { defaultValue: 'Exit Analytics' })}</p>
        <p className="text-xs text-muted-foreground">
          {t('talentexit.analytics.hrReasonNote', { defaultValue: 'Los conteos siguientes provienen de datos administrativos (motivo registrado por RR. HH.). El motivo declarado por el colaborador en la entrevista de salida es un dato independiente y no se mezcla con estos conteos.' })}
        </p>
        {patterns.insufficient_interview_sample && (
          <p className="text-xs text-amber-700" data-testid="tx-analytics-small-sample">
            {t('talentexit.analytics.smallSample', { defaultValue: `Entrevistas completadas: ${patterns.interviews_completed ?? 0}. Aún no hay entrevistas suficientes para establecer tendencias analíticas confiables — se muestran solo conteos administrativos.` })}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Section title={t('talentexit.patterns.byType', { defaultValue: 'By exit type' })} data={patterns.by_exit_type} labelFn={(k) => t(`talentexit.exitTypes.${k}`, { defaultValue: k })} />
      <Section title={t('talentexit.patterns.byReasonHr', { defaultValue: 'Motivo registrado por RR. HH.' })} data={patterns.by_reason} labelFn={(k) => t(`talentexit.reasons.${k}`, { defaultValue: k })} />
      <Section title={t('talentexit.patterns.byDept', { defaultValue: 'By department' })} data={patterns.by_department} />
      <Section title={t('talentexit.patterns.byMonth', { defaultValue: 'By month' })} data={patterns.by_month} />
      </div>
    </div>
  );
}

function SettingsPanel({ settings, onSaved }) {
  const { t } = useTranslation();
  const [years, setYears] = useState(settings?.retention_years || 5);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setYears(settings?.retention_years || 5); }, [settings]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/client/talentexit/settings', { retention_years: Number(years) });
      toast.success(t('talentexit.settings.saved', { defaultValue: 'Settings saved.' }));
      onSaved();
    } catch (e) { toast.error(txError(t, e)); } finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tx-settings">
      <Card className="p-5 shadow-card space-y-4">
        <h4 className="text-sm font-semibold">{t('talentexit.settings.retention', { defaultValue: 'Interview data retention' })}</h4>
        <p className="text-sm text-muted-foreground">{t('talentexit.settings.retentionHint', { defaultValue: 'Raw exit-interview responses stop being readable this many years after case closure. Aggregated patterns remain available.' })}</p>
        <div className="flex items-end gap-3">
          <div className="space-y-1.5">
            <Label>{t('talentexit.settings.years', { defaultValue: 'Years' })}</Label>
            <Input type="number" min={1} max={15} value={years} onChange={(e) => setYears(e.target.value)} className="w-28" data-testid="tx-retention-input" />
          </div>
          <Button onClick={save} disabled={saving || !settings?.caller_is_authorized} data-testid="tx-retention-save">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </div>
        {!settings?.caller_is_authorized && (
          <p className="text-xs text-warning flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" />{t('talentexit.settings.notAuthorizedHint', { defaultValue: 'Only authorized TalentExit users can change retention.' })}</p>
        )}
      </Card>
      <Card className="p-5 shadow-card space-y-3">
        <h4 className="text-sm font-semibold">{t('talentexit.settings.authorized', { defaultValue: 'Authorized TalentExit users' })}</h4>
        <p className="text-sm text-muted-foreground">{t('talentexit.settings.authorizedHint', { defaultValue: 'Only these users can read raw exit-interview responses or record HR-led interviews. CTH Advisors never access raw responses; CTH Admin may use governed exceptional access (mandatory reason, fully audited) for legitimate platform continuity or support situations. Authorization is managed by Corporate Talent Hub and audited.' })}</p>
        <div className="space-y-2" data-testid="tx-authorized-list">
          {(settings?.authorized_users || []).map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded border border-border/60 px-3 py-2">
              <span className="text-sm">{u.name}</span>
              <span className="text-xs text-muted-foreground">{u.email}</span>
            </div>
          ))}
          {(settings?.authorized_users || []).length === 0 && (
            <p className="text-sm text-muted-foreground">{t('talentexit.settings.noAuthorized', { defaultValue: 'No authorized users yet — contact your CTH advisor.' })}</p>
          )}
        </div>
      </Card>
    </div>
  );
}

export default function ClientTalentExit() {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  const [cases, setCases] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [settings, setSettings] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get('/client/talentexit/cases').then((r) => r.data),
        api.get('/client/talentexit/patterns').then((r) => r.data),
        api.get('/client/talentexit/settings').then((r) => r.data),
      ]);
      setCases(c); setPatterns(p); setSettings(s);
    } catch (e) { toast.error(txError(t, e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/client/users').then((r) => setEmployees((r.data || []).filter((u) => u.status !== 'inactive'))).catch(() => {});
  }, []);

  const filtered = useMemo(() => (
    statusFilter === 'all' ? cases : cases.filter((c) => c.status === statusFilter)
  ), [cases, statusFilter]);

  const kpi = useMemo(() => ({
    total: cases.length,
    open: cases.filter((c) => c.status === 'open').length,
    closed: cases.filter((c) => c.status === 'closed').length,
    interviews: cases.filter((c) => ['completed', 'recorded'].includes(c.interview?.status)).length,
  }), [cases]);

  const fmtDate = (v) => (v ? new Date(v.length > 10 ? v : `${v}T12:00:00`).toLocaleDateString(es ? 'es-PA' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

  return (
    <div className="min-h-screen" data-testid="client-talentexit">
      <Topbar title="TalentExit" subtitle={t('talentexit.subtitle', { defaultValue: 'Structured exits, knowledge transfer and closure control' })} />
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label={t('talentexit.kpi.total', { defaultValue: 'Exit cases' })} value={kpi.total} icon={DoorOpen} tone="warning" />
          <KpiCard label={t('talentexit.kpi.open', { defaultValue: 'Open' })} value={kpi.open} icon={ClipboardList} tone="info" />
          <KpiCard label={t('talentexit.kpi.closed', { defaultValue: 'Closed' })} value={kpi.closed} icon={CheckCircle2} tone="success" />
          <KpiCard label={t('talentexit.kpi.interviews', { defaultValue: 'Interviews captured' })} value={kpi.interviews} icon={MessageSquareText} tone="primary" />
        </div>

        {/* Founder UAT 2026-06 — executive visual analytics (real data only) */}
        {cases.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tx-analytics-row">
            <DonutCard
              title={t('talentexit.analytics.statusTitle', { defaultValue: es ? 'Estado de casos de salida' : 'Exit case status' })}
              testid="tx-chart-status"
              accent={SEM.orange}
              data={[
                { name: t('talentexit.filters.open', { defaultValue: es ? 'Abiertos' : 'Open' }), value: kpi.open, color: SEM.orange },
                { name: t('talentexit.filters.closed', { defaultValue: es ? 'Cerrados' : 'Closed' }), value: kpi.closed, color: SEM.green },
              ]}
              emptyText={t('talentexit.analytics.empty', { defaultValue: es ? 'Sin casos registrados.' : 'No cases recorded.' })}
            />
            <ProgressCard
              title={t('talentexit.analytics.interviewTitle', { defaultValue: es ? 'Entrevistas de salida' : 'Exit interviews' })}
              subtitle={t('talentexit.analytics.interviewSubtitle', { defaultValue: es ? 'Capturadas sobre el total de casos' : 'Captured over total cases' })}
              testid="tx-chart-interviews"
              accent={SEM.blue}
              total={kpi.total}
              rows={[
                { label: t('talentexit.kpi.interviews', { defaultValue: es ? 'Entrevistas capturadas' : 'Interviews captured' }), value: kpi.interviews, color: SEM.blue },
                { label: t('talentexit.analytics.noInterview', { defaultValue: es ? 'Sin entrevista' : 'No interview' }), value: Math.max(0, kpi.total - kpi.interviews), color: SEM.slate },
              ]}
              emptyText={t('talentexit.analytics.empty', { defaultValue: es ? 'Sin casos registrados.' : 'No cases recorded.' })}
            />
          </div>
        )}

        <Tabs defaultValue="cases">
          <TabsList>
            <TabsTrigger value="cases" data-testid="tx-tab-cases">{t('talentexit.tabs.cases', { defaultValue: 'Cases' })}</TabsTrigger>
            <TabsTrigger value="patterns" data-testid="tx-tab-patterns">{t('talentexit.tabs.analytics', { defaultValue: 'Exit Analytics' })}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tx-tab-settings">{t('talentexit.tabs.settings', { defaultValue: 'Settings' })}</TabsTrigger>
          </TabsList>

          <TabsContent value="cases" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-2">
                {['all', 'open', 'closed'].map((sf) => (
                  <Button key={sf} size="sm" variant={statusFilter === sf ? 'default' : 'outline'} onClick={() => setStatusFilter(sf)} data-testid={`tx-filter-${sf}`}>
                    {t(`talentexit.filters.${sf}`, { defaultValue: sf })}
                  </Button>
                ))}
              </div>
              <Button onClick={() => setCreateOpen(true)} className="gap-1.5" data-testid="tx-new-case-btn">
                <Plus className="h-4 w-4" /> {t('talentexit.actions.newCase', { defaultValue: 'New exit case' })}
              </Button>
            </div>

            <Card className="shadow-card overflow-hidden">
              {loading ? (
                <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center space-y-2" data-testid="tx-empty">
                  <Users className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">{t('talentexit.emptyCases', { defaultValue: 'No exit cases yet. Create one to start a structured exit process.' })}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[860px]">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left text-[13px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.employee', { defaultValue: 'Employee' })}</th>
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.department', { defaultValue: 'Department' })}</th>
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.exitType', { defaultValue: 'Exit type' })}</th>
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.lastDay', { defaultValue: 'Last day' })}</th>
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.interview', { defaultValue: 'Interview' })}</th>
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.checklist', { defaultValue: 'Checklist' })}</th>
                        <th className="px-4 py-3 font-medium">{t('talentexit.fields.status', { defaultValue: 'Status' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c) => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                            onClick={() => setDetailId(c.id)} data-testid={`tx-case-row-${c.id}`}>
                          <td className="px-4 py-3 font-medium">{c.snapshot?.user_name}</td>
                          <td className="px-4 py-3 text-muted-foreground">{c.snapshot?.department_name || '—'}</td>
                          <td className="px-4 py-3">{t(`talentexit.exitTypes.${c.exit_type}`, { defaultValue: c.exit_type })}</td>
                          <td className="px-4 py-3">{fmtDate(c.last_working_day)}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={IV_BADGE[c.interview?.status] || IV_BADGE.not_started}>
                              {t(`talentexit.interviewStatus.${c.interview?.status || 'not_started'}`)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{c.checklist_done}/{c.checklist_total}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={c.status === 'closed' ? 'bg-success/10 text-success border-success/20' : 'bg-info/10 text-info border-info/20'}>
                              {t(`talentexit.status.${c.status}`, { defaultValue: c.status })}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="patterns" className="mt-4">
            <PatternsPanel patterns={patterns} />
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <SettingsPanel settings={settings} onSaved={load} />
          </TabsContent>
        </Tabs>
      </div>

      <CreateCaseDialog open={createOpen} onOpenChange={setCreateOpen} employees={employees} onCreated={load} />
      {detailId && (
        <TalentExitCaseDetail
          caseId={detailId}
          onClose={() => { setDetailId(null); load(); }}
        />
      )}
    </div>
  );
}
